import { NavbarSkeleton } from "../app/navbars/Navbar";
import { GPT_HEADER_ROW } from "../gpt/layout";
import Skeleton from "./Skeleton";
import SkeletonTextLine from "./SkeletonTextLine";

// The four conversation starters (Search / Understand / Analyze / Write) — see
// GPTPrePrompts. Their labels differ in length, so the silhouette varies the
// bar width rather than drawing four identical rows. Measured off the rendered
// labels at the real 15px ramp, then widened ~15%: English is the shortest of
// the eight locales and a bar that stops short of the incoming text reads as a
// jump when the page swaps in.
const PRE_PROMPT_LABEL_WIDTHS = [55, 94, 62, 43] as const;

/**
 * Cold-boot silhouette of the JuisciGPT landing (GPTNewChatPage in its
 * non-conversation state) — the B2C home, so a root boot for a user without a
 * pinned room redirects there (`getInitialRoute`).
 *
 * Mirrors the real landing piece for piece so the page fades in over a matching
 * shape. Pure render (no queries, no auth reads), so mounting it cannot
 * mis-render as logged-out the way the real page would.
 */
export default function JuisciGPTBootSkeleton(): React.JSX.Element {
  return (
    <div className="flex h-full flex-col bg-ds-cream">
      {/* Burger row. Two levels like the real page: ScrollableLayout puts the
          safe area on the header slot and GPT_HEADER_ROW sits inside it —
          folding them into one element would override the row's own top
          padding and lift the glyph 12px above where the page lands it. */}
      <div style={{ paddingTop: "var(--safe-area-top)" }}>
        <div className={`${GPT_HEADER_ROW} justify-between`}>
          <Skeleton $variant="rounded" width={24} height={24} />
          {/* ProfilePicture height 32, same as every other page's header. */}
          <Skeleton $variant="circular" width={32} height={32} />
        </div>
      </div>

      {/* Landing column — mirrors GPTConversationWrapper's `bg-ds-cream pt-4`
          and the `px-5` content stack. */}
      <div className="flex flex-1 flex-col overflow-hidden px-5 pt-4">
        {/* Greeting: two Petrona lines (32/36) centred in the top half. The
            two halves are `min-h-0 flex-1` exactly as on the real landing,
            so the silhouette puts both blocks where the page will. */}
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center py-6">
          <SkeletonTextLine width={296} fontSize={32} lineHeight={36} />
          <SkeletonTextLine width={208} fontSize={32} lineHeight={36} />
        </div>

        {/* Conversation starters: content-hugging chips, each an icon
            (size-5 = 20px) + a 15/20 label inside `gap-3 rounded-lg px-3
            py-2.5` — 40px per row on a 4px stack gap, so the four of them
            stand exactly where the real list lands. */}
        <div className="flex min-h-0 flex-1 flex-col items-start justify-end-safe gap-1">
          {PRE_PROMPT_LABEL_WIDTHS.map((labelWidth, index) => (
            <div
              key={index}
              className="flex shrink-0 items-center gap-3 px-3 py-2.5"
            >
              <Skeleton $variant="rounded" width={20} height={20} />
              <SkeletonTextLine
                width={labelWidth}
                fontSize={15}
                lineHeight={20}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Composer — the real one is fixed at the bottom of the viewport; here it
          closes the column. The navbar below absorbs the home-indicator inset. */}
      <div className="px-5 pt-2.5" style={{ paddingBottom: "16px" }}>
        {/* ChatInput: rounded-2xl card, one 21px text line under `px-4 py-3`,
            then the 34px actions row — "+" and the mode picker
            on the left, mic + send on the right. */}
        <div className="flex flex-col rounded-2xl bg-[rgba(253,253,253,0.84)] shadow-[0px_2px_8px_rgba(0,0,0,0.08)]">
          <div className="px-4 py-3">
            <SkeletonTextLine width={192} fontSize={15} lineHeight={21} />
          </div>
          <div className="flex items-center justify-between p-2  ">
            <div className="flex items-center gap-1">
              <Skeleton $variant="rounded" width={34} height={34} />
              <Skeleton $variant="rounded" width={80} height={32} />
            </div>
            <div className="flex items-center gap-1">
              <Skeleton $variant="rounded" width={34} height={34} />
              <Skeleton $variant="rounded" width={34} height={34} />
            </div>
          </div>
        </div>
      </div>

      <NavbarSkeleton />
    </div>
  );
}
